"use client";

import { useActionState, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { CheckCircle2, ChevronDown, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";

import { createInquiryAction } from "@/app/(public)/catering/actions";
import { useCateringPublicConfig } from "@/components/order/catering-fulfillment-panel";
import {
  isRomaBranch,
  validateRomaBranchField,
  type RomaBranch,
} from "@/lib/roma-branch";
import { RomaBranchChoice } from "@/components/roma/roma-branch-choice";
import {
  analytics,
  getFormControlFieldKey,
  getFormControlValidityErrorType,
  getLeadAttribution,
  getPartySizeBucket,
} from "@/lib/analytics";
import {
  buildCateringFaqItemsForSite,
  buildCateringMenuItemsForSite,
  type LeadRouteCateringMenuItem,
} from "@/lib/lead-route-schema";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";

const eventTypes = [
  { value: "catering", label: "Catering trays" },
  { value: "corporate", label: "Office meal" },
  { value: "birthday_anniversary", label: "Birthday or anniversary" },
  { value: "holiday_party", label: "Holiday party" },
  { value: "rehearsal_dinner", label: "Rehearsal dinner" },
  { value: "private_party", label: "Private party" },
] as const;

type CateringEventType = (typeof eventTypes)[number]["value"];

const isCateringEventType = (value: string): value is CateringEventType =>
  eventTypes.some((type) => type.value === value);

type CateringFormState = { success: boolean; error?: string } | null;
type FulfillmentPreference = "delivery" | "pickup";
type SelectedCateringItem = {
  name: string;
  price: string;
  quantity: number;
};

const fieldGroups: Record<string, string> = {
  customerName: "contact",
  customer_name: "contact",
  email: "contact",
  phone: "contact",
  eventType: "event",
  event_type: "event",
  eventDate: "event",
  event_date: "event",
  eventTime: "event",
  event_time: "event",
  romaBranch: "event",
  roma_branch: "event",
  guestCount: "party_size",
  guest_count: "party_size",
  fulfillmentPreference: "delivery",
  streetAddress: "delivery",
  unit: "delivery",
  city: "delivery",
  state: "delivery",
  zipCode: "delivery",
  message: "details",
  dietaryRestrictions: "details",
  additionalDetails: "details",
};

const fieldKeys: Record<string, string> = {
  customerName: "customer_name",
  eventType: "event_type",
  eventDate: "event_date",
  eventTime: "event_time",
  romaBranch: "roma_branch",
  guestCount: "guest_count",
  fulfillmentPreference: "fulfillment_preference",
  streetAddress: "street_address",
  zipCode: "zip_code",
  dietaryRestrictions: "dietary_restrictions",
  additionalDetails: "additional_details",
};

const saffronInputClass =
  "h-[48px] w-full rounded-[10px] border border-[#6d6d6d] bg-white px-4 text-[16px] leading-none text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#6f6f6f] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

const saffronTextareaClass =
  "min-h-[132px] w-full resize-none rounded-[10px] border border-[#6d6d6d] bg-white px-4 py-3 text-[16px] leading-6 text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#6f6f6f] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

type FooterLink = {
  href: string;
  label: string;
  external?: boolean;
};

function isExternalHref(href: string) {
  return /^(https?:|mailto:|tel:)/.test(href);
}

function buildFooterLinks({
  homeHref,
  siteConfig,
}: {
  homeHref: string;
  siteConfig: ReturnType<typeof useMarketingLocationContext>["siteConfig"];
}): FooterLink[] {
  const contactHref = "/contact";

  return [
    { href: homeHref, label: "Home" },
    {
      href: siteConfig.menuUrl,
      label: "Menu",
      external: isExternalHref(siteConfig.menuUrl),
    },
    { href: "/catering", label: "Catering" },
    { href: "/private-events", label: "Private Events" },
    {
      href: siteConfig.reservationUrl,
      label: "Reservations",
      external: isExternalHref(siteConfig.reservationUrl),
    },
    {
      href: contactHref,
      label: "Contact",
      external: isExternalHref(contactHref),
    },
  ];
}

const cateringGuideLinks = [
  {
    href: "/private-events/group-dining-catering-guide",
    label: "Group dining and catering guide",
    description: "Compare catering trays, hosted meals, and private-event inquiry options.",
  },
  {
    href: "/best/catering-southern-highlands-las-vegas",
    label: "Catering in Southern Highlands",
    description: "Tray ideas, group meals, and planning details.",
  },
  {
    href: "/best/corporate-catering-las-vegas",
    label: "Corporate catering in Las Vegas",
    description: "Office meals, client lunches, and hosted work events.",
  },
] as const;

const romaCateringGuideLinks = [
  {
    href: "#catering-request-form",
    label: "Start a catering request",
    description: "Send the Roma Kitchen team the event date, guest count, and dishes you have in mind.",
  },
  {
    href: "/best/italian-catering-las-vegas",
    label: "Italian catering menu and pasta trays for Las Vegas and Henderson",
    description: "Read the Roma guide for pasta trays, office meals, pickup, and delivery planning.",
  },
  {
    href: "/best/pasta-las-vegas",
    label: "Pasta for groups",
    description: "Use the pasta guide to pick cacio e pepe, lobster ravioli, and signature plates.",
  },
  {
    href: "/best/italian-restaurant-southern-highlands-las-vegas",
    label: "Southern Highlands pickup",
    description: "Route southwest Las Vegas trays through the Decatur Blvd kitchen.",
  },
  {
    href: "/best/italian-restaurant-henderson-nv",
    label: "Henderson pickup",
    description: "Route Henderson and east-valley trays through the Horizon Ridge kitchen.",
  },
  {
    href: "/private-events",
    label: "Plan a private event",
    description: "Use the private-events form for hosted meals, rehearsal dinners, and celebrations.",
  },
] as const;

const cateringSnapshot = [
  {
    label: "Best for",
    detail:
      "Pickup trays, office meals, birthdays, watch parties, neighborhood gatherings, and group food in Southern Highlands.",
  },
  {
    label: "Menu direction",
    detail:
      "Pizza bundles, wings, tenders, sliders, sandwich platters, fries, salads, and party-friendly trays.",
  },
] as const;

const romaCateringSnapshot = [
  {
    label: "Best for",
    detail:
      "Italian catering for pasta trays, chicken parmigiana trays, office meals, birthdays, rehearsal meals, and group dinners near Southern Highlands or Henderson.",
  },
  {
    label: "Menu direction",
    detail:
      "Antipasti, salads, pasta trays, chicken parmigiana, eggplant parmigiano, sides, and desserts.",
  },
  {
    label: "Pickup kitchens",
    detail:
      "Southern Highlands at 10420 S Decatur Blvd #100 or Henderson at 1550 W Horizon Ridge Pkwy Ste P.",
  },
] as const;

type CateringMenuItem = LeadRouteCateringMenuItem;

function getLocalDateValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isValidDateValue(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isValidTimeValue(value: string) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function getOptionalFormValue(formData: FormData, key: string) {
  return String(formData.get(key) || "").trim();
}

function formatCateringItemName(name: string) {
  return name.replace("WIngs", "Wings");
}

function getCateringPickKey(item: CateringMenuItem) {
  return `${item.sectionTitle}-${item.name}`;
}

function formatCateringRequestLine(item: CateringMenuItem, count: number) {
  const itemName = formatCateringItemName(item.name);
  const quantity = count > 1 ? ` x${count}` : "";

  return `Interested in: ${itemName} (${item.price})${quantity}.`;
}

function normalizeFulfillmentPreference(
  value: string,
): FulfillmentPreference | undefined {
  const normalized = value.trim().toLowerCase();
  if (normalized === "delivery" || normalized === "pickup") {
    return normalized;
  }

  return undefined;
}

function getSelectedCateringItems(
  pickCounts: Record<string, number>,
  cateringItems: CateringMenuItem[],
): SelectedCateringItem[] {
  return cateringItems
    .map((item) => {
      const quantity = pickCounts[getCateringPickKey(item)] ?? 0;
      return {
        name: formatCateringItemName(item.name),
        price: item.price,
        quantity,
      };
    })
    .filter((item) => item.quantity > 0);
}

export function CateringPageView(_props: {
  /** Normalized dish name -> photo URL from Convex ordering. Kept for page API compatibility. */
  photoLookup?: Record<string, string>;
}) {
  void _props;

  const { isDefaultLocation, location, restaurantCopy, siteConfig } =
    useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  // Tenants with live catering ordering lead with "Order Catering"; the
  // custom-event inquiry form below stays as the secondary path.
  const cateringConfig = useCateringPublicConfig();
  const cateringOrderingLive = Boolean(cateringConfig?.enabled);
  const cateringItems = useMemo(
    () => buildCateringMenuItemsForSite(siteConfig),
    [siteConfig],
  );
  const cateringMenuSections = useMemo(() => {
    const sections = new Map<string, CateringMenuItem[]>();

    for (const item of cateringItems) {
      const sectionItems = sections.get(item.sectionTitle) ?? [];
      sectionItems.push(item);
      sections.set(item.sectionTitle, sectionItems);
    }

    return Array.from(sections, ([title, items]) => ({ title, items }));
  }, [cateringItems]);
  const faqItems = useMemo(
    () => buildCateringFaqItemsForSite(siteConfig),
    [siteConfig],
  );
  const isRoma = siteConfig.name === "Roma Kitchen";
  const guideLinks = isRoma ? romaCateringGuideLinks : cateringGuideLinks;
  const neutralCateringSnapshot = useMemo(
    () => [
      {
        label: "Best for",
        detail: `Pickup trays, office meals, birthdays, watch parties, and group food in ${siteConfig.address.city}.`,
      },
      {
        label: "Menu direction",
        detail:
          "Shareable platters, salads, sides, and party-friendly trays sized for groups.",
      },
    ],
    [siteConfig.address.city],
  );
  const cateringSnapshotItems = isDefaultLocation
    ? cateringSnapshot
    : isRoma
      ? romaCateringSnapshot
      : neutralCateringSnapshot;
  const phoneDisplay = restaurantCopy.phoneDisplay;
  const phoneHref = restaurantCopy.phoneHref;
  const addressLine = `${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}`;
  // Roma serves its home at "/"; /locations/<slug> is a canonicalized
  // duplicate of it (see brandHref in src/lib/public-chrome.ts).
  const homeHref =
    isDefaultLocation || location.restaurantId === "roma-kitchen"
      ? "/"
      : `/locations/${location.slug}`;
  const footerLogoSrc = siteConfig.logoImage || "/android-chrome-512x512.png";
  const footerLinks = useMemo(
    () => buildFooterLinks({ homeHref, siteConfig }),
    [homeHref, siteConfig],
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [minEventDate, setMinEventDate] = useState("");
  const [eventType, setEventType] = useState<CateringEventType>("catering");
  const [romaBranch, setRomaBranch] = useState<"" | RomaBranch>("");
  const [romaBranchError, setRomaBranchError] = useState<string | undefined>(
    undefined,
  );
  const [addedPickCounts, setAddedPickCounts] = useState<Record<string, number>>({});
  const addedPickCountsRef = useRef<Record<string, number>>({});
  const hasStartedInquiryRef = useRef(false);
  const lastAttemptedAtRef = useRef(0);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setMinEventDate(getLocalDateValue());
  }, []);

  function markInquiryStarted(type = eventType) {
    if (hasStartedInquiryRef.current) return;

    hasStartedInquiryRef.current = true;
    analytics.formStarted({
      category: "form",
      form_type: "catering_inquiry",
      source: "catering_form",
      inquiry_type: type,
    });
    analytics.inquiryStarted(type, "catering_form");
  }

  function getCateringFunnelProperties(
    properties: Record<string, string | number | boolean | undefined> = {},
  ) {
    return {
      form_type: "catering_inquiry",
      source: "catering_form",
      inquiry_type: eventType,
      ...properties,
    };
  }

  function trackCateringAttempted(type = eventType, guestCount?: string | number) {
    const now = Date.now();
    if (now - lastAttemptedAtRef.current < 750) return;

    lastAttemptedAtRef.current = now;
    analytics.formAttempted(
      getCateringFunnelProperties({
        step: "details",
        inquiry_type: type,
        party_size_bucket: guestCount ? getPartySizeBucket(guestCount) : undefined,
      }),
    );
  }

  function trackCateringValidationFailure(
    fieldKey: string,
    errorType: string,
    type = eventType,
    guestCount?: string | number,
  ) {
    const normalizedFieldKey = fieldKeys[fieldKey] ?? fieldKey;

    analytics.formValidationFailed(
      getCateringFunnelProperties({
        step: "details",
        inquiry_type: type,
        field_group:
          fieldGroups[fieldKey] ?? fieldGroups[normalizedFieldKey] ?? "details",
        field_key: normalizedFieldKey,
        error_type: errorType,
        party_size_bucket: guestCount ? getPartySizeBucket(guestCount) : undefined,
      }),
    );
  }

  function addCateringPickToRequest(item: CateringMenuItem) {
    const textarea = messageInputRef.current;
    const itemName = formatCateringItemName(item.name);
    const pickKey = getCateringPickKey(item);
    const nextCount = (addedPickCountsRef.current[pickKey] ?? 0) + 1;
    const nextCounts = {
      ...addedPickCountsRef.current,
      [pickKey]: nextCount,
    };
    const linePrefix = `Interested in: ${itemName} (${item.price})`;
    const addition = formatCateringRequestLine(item, nextCount);

    addedPickCountsRef.current = nextCounts;
    setAddedPickCounts(nextCounts);

    markInquiryStarted();
    analytics.track("catering_item_added_to_request", {
      category: "form",
      source: "catering_menu",
      item_name: itemName,
      item_price: item.price,
      item_count: nextCount,
    });

    if (!textarea) return;

    const existingLines = textarea.value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith(linePrefix));
    textarea.value = [...existingLines, addition].join("\n");
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
    textarea.scrollIntoView({ behavior: "smooth", block: "center" });
    textarea.focus({ preventScroll: true });
  }

  const [formState, formAction, isPending] = useActionState(
    async (_prevState: CateringFormState, formData: FormData) => {
      const customerName = getOptionalFormValue(formData, "customerName");
      const email = getOptionalFormValue(formData, "email");
      const phone = getOptionalFormValue(formData, "phone");
      const eventDate = getOptionalFormValue(formData, "eventDate");
      const eventTime = getOptionalFormValue(formData, "eventTime");
      const guestCount = getOptionalFormValue(formData, "guestCount");
      const rawMessage = getOptionalFormValue(formData, "message");
      const smsConsent = formData.get("smsConsent") === "on";
      const parsedGuestCount = Number.parseInt(guestCount, 10);
      const eventTypeValue = String(formData.get("eventType") || eventType);
      const submittedEventType = isCateringEventType(eventTypeValue)
        ? eventTypeValue
        : eventType;
      const fulfillmentPreference = getOptionalFormValue(
        formData,
        "fulfillmentPreference",
      );
      const streetAddress = getOptionalFormValue(formData, "streetAddress");
      const unit = getOptionalFormValue(formData, "unit");
      const city = getOptionalFormValue(formData, "city");
      const state = getOptionalFormValue(formData, "state");
      const zipCode = getOptionalFormValue(formData, "zipCode");
      const dietaryRestrictions = getOptionalFormValue(
        formData,
        "dietaryRestrictions",
      );
      const additionalDetails = getOptionalFormValue(formData, "additionalDetails");
      const addressLine = [streetAddress, unit, city, state, zipCode]
        .filter(Boolean)
        .join(", ");
      const normalizedFulfillmentPreference =
        normalizeFulfillmentPreference(fulfillmentPreference);
      const deliveryAddress =
        streetAddress && city && state && zipCode
          ? {
              streetAddress,
              ...(unit ? { unit } : {}),
              city,
              state,
              zipCode,
            }
          : undefined;
      const selectedItems = getSelectedCateringItems(
        addedPickCountsRef.current,
        cateringItems,
      );
      const message = [
        rawMessage,
        fulfillmentPreference
          ? `Fulfillment preference: ${fulfillmentPreference}`
          : "",
        addressLine ? `Delivery address: ${addressLine}` : "",
        dietaryRestrictions
          ? `Dietary restrictions or allergies: ${dietaryRestrictions}`
          : "",
        additionalDetails ? `Additional details: ${additionalDetails}` : "",
      ]
        .filter(Boolean)
        .join("\n\n");

      markInquiryStarted(submittedEventType);
      trackCateringAttempted(submittedEventType, guestCount);

      const romaBranchValidationError = validateRomaBranchField({
        isRoma,
        value: romaBranch,
      });
      setRomaBranchError(romaBranchValidationError);
      if (romaBranchValidationError) {
        trackCateringValidationFailure(
          "romaBranch",
          "missing_required",
          submittedEventType,
          guestCount,
        );
        toast.error(romaBranchValidationError);
        return { success: false, error: romaBranchValidationError };
      }

      if (
        !customerName ||
        !email ||
        !phone ||
        !eventDate ||
        !eventTime ||
        !guestCount ||
        !rawMessage
      ) {
        [
          ["customerName", customerName],
          ["email", email],
          ["phone", phone],
          ["eventDate", eventDate],
          ["eventTime", eventTime],
          ["guestCount", guestCount],
          ["message", rawMessage],
        ]
          .filter(([, value]) => !value)
          .forEach(([fieldKey]) =>
            trackCateringValidationFailure(
              fieldKey,
              "missing_required",
              submittedEventType,
              guestCount,
            ),
          );
        toast.error("Please complete the required catering details.");
        return {
          success: false,
          error: "Please fill out every required field before submitting.",
        };
      }

      if (!isValidDateValue(eventDate) || eventDate < getLocalDateValue()) {
        trackCateringValidationFailure(
          "eventDate",
          "invalid_date",
          submittedEventType,
          guestCount,
        );
        toast.error("Please choose today or a future catering date.");
        return {
          success: false,
          error: "Please choose today or a future date.",
        };
      }

      if (!isValidTimeValue(eventTime)) {
        trackCateringValidationFailure(
          "eventTime",
          "invalid_time",
          submittedEventType,
          guestCount,
        );
        toast.error("Please enter a valid catering time.");
        return { success: false, error: "Please enter a valid time." };
      }

      if (!Number.isFinite(parsedGuestCount) || parsedGuestCount < 1) {
        trackCateringValidationFailure(
          "guestCount",
          "invalid_number",
          submittedEventType,
          guestCount,
        );
        toast.error("Please enter a valid guest count.");
        return { success: false, error: "Please enter a valid guest count." };
      }

      const result = await createInquiryAction({
        customerName,
        email,
        phone,
        eventType: submittedEventType,
        eventDate,
        eventTime,
        guestCount: parsedGuestCount,
        message,
        fulfillmentPreference: normalizedFulfillmentPreference,
        deliveryAddress,
        dietaryRestrictions: dietaryRestrictions || undefined,
        additionalDetails: additionalDetails || undefined,
        selectedItems: selectedItems.length ? selectedItems : undefined,
        smsConsent,
        attribution: getLeadAttribution("catering_form"),
        romaBranch: isRoma && isRomaBranch(romaBranch) ? romaBranch : undefined,
      });

      if (result.success) {
        const leadProperties = {
          lead_id: result.id,
          lead_type: "catering",
          inquiry_type: submittedEventType,
          source: "catering_form",
          party_size_bucket: getPartySizeBucket(parsedGuestCount),
        };
        analytics.formSubmitSucceeded(
          getCateringFunnelProperties({
            step: "server_action",
            ...leadProperties,
          }),
        );
        analytics.inquirySubmitted(submittedEventType, "catering_form", leadProperties);
        analytics.leadCaptured("catering", "catering_form", leadProperties);
        analytics.cateringInquirySubmitted({
          ...leadProperties,
          inquiry_type: submittedEventType,
        });
        toast.success("Catering inquiry submitted. We will be in touch soon.");
        setIsSubmitted(true);
      } else {
        analytics.formSubmitFailed(
          getCateringFunnelProperties({
            step: "server_action",
            inquiry_type: submittedEventType,
            error_type: "server_rejected",
            party_size_bucket: getPartySizeBucket(parsedGuestCount),
          }),
        );
        toast.error(result.error || "Something went wrong. Please try again.");
      }

      return result;
    },
    null,
  );

  if (isSubmitted) {
    return (
      <main
        data-public-page-brand={pageBrand}
        data-roma-lead-page={isRoma ? "light" : undefined}
        className="roma-readable-lead-page min-h-[80dvh] bg-white px-4 py-14 text-[#1f1f1f]"
      >
        <section className="mx-auto max-w-[560px] rounded-[22px] border border-[#e3e3e3] bg-white px-6 py-10 text-center shadow-[0_20px_60px_-46px_rgba(0,0,0,0.45)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-serif text-[26px] font-semibold leading-8">
            Catering inquiry received
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#555]">
            Thanks. The {restaurantCopy.shortName} team will review your date, guest count, and
            menu notes, then follow up with the next practical step.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              className="h-12 rounded-[10px] border border-[#d6d6d6] bg-white px-5 text-[15px] font-semibold text-[#1f1f1f] transition-colors hover:bg-[#f7f7f7] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
              onClick={() => {
                setIsSubmitted(false);
                setEventType("catering");
                addedPickCountsRef.current = {};
                setAddedPickCounts({});
              }}
            >
              Send another inquiry
            </button>
            <Link
              href={`tel:${phoneHref}`}
              className="roma-lead-primary-action inline-flex h-12 items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-saffron-deep)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/25"
              onClick={() =>
                analytics.phoneClicked({
                  location: "catering_success",
                  cta_text: "Call restaurant",
                  destination_url: `tel:${phoneHref}`,
                  lead_type: "catering",
                })
              }
            >
              Call {phoneDisplay}
              <Phone className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      data-public-page-brand={pageBrand}
      data-roma-lead-page={isRoma ? "light" : undefined}
      className="roma-readable-lead-page min-h-screen w-full overflow-x-hidden bg-[#f7f5f2] text-[#1f1f1f]"
    >
      <section
        id="catering-inquiry"
        className="mx-auto w-full max-w-[720px] scroll-mt-site-header px-4 pb-8 pt-8 sm:px-6 md:pb-12 md:pt-10"
      >
        <h1 className="font-serif text-[22px] font-semibold leading-7 text-[#151515]">
          {restaurantCopy.cateringTitle}
        </h1>
        <p className="mt-4 max-w-[360px] text-[16px] leading-[1.55] text-[#202020]">
          {restaurantCopy.cateringIntro}
        </p>
        {cateringOrderingLive ? (
          <div
            data-catering-order-cta
            className="mt-6 rounded-[14px] border border-[#e3e3e3] bg-white p-4"
          >
            <p className="text-[15px] font-semibold leading-6 text-[#1f1f1f]">
              Order catering online
            </p>
            <p className="mt-1 text-[14px] leading-6 text-[#555]">
              Choose {cateringConfig?.delivery.enabled ? "pickup or delivery" : "a pickup location"}, pick a time at least{" "}
              {cateringConfig?.minimumAdvanceHours} hours ahead, and pay securely online.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Link
                href="/order/catering"
                className="roma-lead-primary-action inline-flex h-12 items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-saffron-deep)]"
                onClick={() =>
                  analytics.ctaClicked("order_catering", "catering_page_hero", {
                    destination_url: "/order/catering",
                  })
                }
              >
                Order Catering
              </Link>
              <a
                href="#catering-request-form"
                className="inline-flex h-12 items-center justify-center rounded-[10px] border border-[#d6d6d6] bg-white px-5 text-[15px] font-semibold text-[#1f1f1f] transition-colors hover:bg-[#f7f7f7]"
              >
                Plan a custom event instead
              </a>
            </div>
          </div>
        ) : null}

        <form
          id="catering-request-form"
          action={formAction}
          className="mt-6 space-y-7"
          onFocusCapture={() => markInquiryStarted()}
          onInvalidCapture={(event) => {
            const formData = new FormData(event.currentTarget);
            const guestCount = String(formData.get("guestCount") || "");
            const fieldKey = getFormControlFieldKey(event.target);

            trackCateringAttempted(eventType, guestCount);
            trackCateringValidationFailure(
              fieldKey,
              getFormControlValidityErrorType(event.target),
              eventType,
              guestCount,
            );
          }}
          onPointerDownCapture={() => markInquiryStarted()}
          onSubmitCapture={(event) => {
            const formData = new FormData(event.currentTarget);
            const eventTypeValue = String(formData.get("eventType") || eventType);
            const submittedEventType = isCateringEventType(eventTypeValue)
              ? eventTypeValue
              : eventType;

            trackCateringAttempted(
              submittedEventType,
              String(formData.get("guestCount") || ""),
            );
          }}
        >
          {isRoma ? (
            <RomaBranchChoice
              legend="Which Roma?"
              intro="Tell us which kitchen should prepare your order."
              value={romaBranch}
              onChange={setRomaBranch}
              error={romaBranchError}
              className="mb-6"
            />
          ) : null}

          {formState?.error ? (
            <div
              role="alert"
              className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-[14px] leading-6 text-red-800"
            >
              {formState.error}
            </div>
          ) : null}

          <input type="hidden" name="eventType" value={eventType} />

          <SaffronField label="Name *" htmlFor="customerName">
            <input
              id="customerName"
              name="customerName"
              required
              autoComplete="name"
              placeholder="e.g. Maria Rossi"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField label="Email *" htmlFor="email">
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              spellCheck={false}
              placeholder="you@email.com"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField label="Phone *" htmlFor="phone">
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              placeholder="Your phone number"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField
            label="Date and time you need catering *"
            htmlFor="eventDate"
          >
            <input
              id="eventDate"
              name="eventDate"
              type="date"
              min={minEventDate}
              required
              autoComplete="off"
              data-testid="catering-event-date"
              disabled={isPending}
              className={saffronInputClass}
            />
            <input
              id="eventTime"
              name="eventTime"
              type="time"
              aria-label="Time you need catering"
              step="900"
              required
              autoComplete="off"
              data-testid="catering-event-time"
              disabled={isPending}
              className={`${saffronInputClass} mt-4`}
            />
          </SaffronField>

          <SaffronField
            label="Number of people you are catering for *"
            htmlFor="guestCount"
          >
            <select
              id="guestCount"
              name="guestCount"
              required
              disabled={isPending}
              className={`${saffronInputClass} appearance-auto`}
              defaultValue="9"
            >
              <option value="9">1-9</option>
              <option value="19">10-19</option>
              <option value="29">20-29</option>
              <option value="39">30-39</option>
              <option value="49">40-49</option>
              <option value="50">50+</option>
            </select>
          </SaffronField>

          <SaffronField label="Pickup or delivery *" htmlFor="fulfillmentPreference">
            <select
              id="fulfillmentPreference"
              name="fulfillmentPreference"
              required
              disabled={isPending}
              className={`${saffronInputClass} appearance-auto`}
              defaultValue="Delivery"
            >
              <option>Delivery</option>
              <option>Pickup</option>
            </select>
          </SaffronField>

          <SaffronField label="What would you like to order? *" htmlFor="message">
            <textarea
              id="message"
              ref={messageInputRef}
              name="message"
              required
              rows={5}
              placeholder="Tell us what you would like to order"
              disabled={isPending}
              className={saffronTextareaClass}
            />
          </SaffronField>

          <SaffronField label="Street Address" htmlFor="streetAddress">
            <input
              id="streetAddress"
              name="streetAddress"
              autoComplete="street-address"
              placeholder="Street Address"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField label="Unit, suite, floor, etc.">
            <input
              id="unit"
              name="unit"
              autoComplete="address-line2"
              placeholder="Unit, suite, floor, etc. (optional)"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField label="City / State" htmlFor="city">
            <div className="grid grid-cols-[minmax(0,1fr)_92px] gap-3">
              <input
                id="city"
                name="city"
                autoComplete="address-level2"
                placeholder="City"
                disabled={isPending}
                className={saffronInputClass}
              />
              <input
                id="state"
                name="state"
                autoComplete="address-level1"
                placeholder="State"
                maxLength={2}
                disabled={isPending}
                className={saffronInputClass}
              />
            </div>
          </SaffronField>

          <SaffronField label="Zip Code" htmlFor="zipCode">
            <input
              id="zipCode"
              name="zipCode"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="Zip Code"
              disabled={isPending}
              className={saffronInputClass}
            />
          </SaffronField>

          <SaffronField
            label="Dietary restrictions or allergies"
            htmlFor="dietaryRestrictions"
          >
            <textarea
              id="dietaryRestrictions"
              name="dietaryRestrictions"
              rows={4}
              placeholder="Dietary restrictions or allergies"
              disabled={isPending}
              className={saffronTextareaClass}
            />
          </SaffronField>

          <SaffronField
            label="Additional details or special instructions"
            htmlFor="additionalDetails"
          >
            <textarea
              id="additionalDetails"
              name="additionalDetails"
              rows={4}
              placeholder="Additional details or special instructions"
              disabled={isPending}
              className={saffronTextareaClass}
            />
          </SaffronField>

          <p className="text-sm text-[#565656]">We’ll reply to your inquiry by email.</p>

          <button
            type="submit"
            className="roma-lead-primary-action flex h-[50px] w-full items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-5 text-[13px] font-bold uppercase tracking-[0.02em] text-[#0d0d0d] transition-colors hover:bg-[var(--roma-saffron-deep)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/25 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={isPending}
            data-testid="catering-submit"
            data-track="catering_submit"
            data-track-location="catering_form"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              "Send Catering Request"
            )}
          </button>
        </form>

      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 pt-2 sm:px-6 md:pb-12">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Catering menu
          </p>
          <h2 className="mt-2 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
            Full catering menu
          </h2>
          <p className="mt-3 max-w-[420px] text-[15px] leading-6 text-[#4d4d4d]">
            Add any tray or bundle to your request and we&apos;ll include it in
            the catering notes.
          </p>

          <div className="mt-6 space-y-3">
            {cateringMenuSections.map((section) => (
              <details
                key={section.title}
                className="group overflow-hidden rounded-[14px] border border-[#d8d8d8] bg-white shadow-sm"
              >
                <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-left marker:hidden [&::-webkit-details-marker]:hidden">
                  <span>
                    <span className="block text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
                      {section.title}
                    </span>
                    <span className="mt-0.5 block text-[13px] leading-5 text-[#666]">
                      {section.items.length} menu options
                    </span>
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 text-[#606060] transition-transform group-open:rotate-180"
                  />
                </summary>
                <div className="divide-y divide-[#ededed] border-t border-[#ededed] px-4">
                  {section.items.map((item) => {
                    const addedCount =
                      addedPickCounts[getCateringPickKey(item)] ?? 0;

                    return (
                      <article
                        key={`${item.sectionTitle}-${item.name}`}
                        className="py-4 first:pt-4 last:pb-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex min-w-0 items-start gap-3">
                            {item.image ? (
                              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-[10px] bg-[#f1eee9] sm:h-[76px] sm:w-[76px]">
                                <Image
                                  src={item.image}
                                  alt={formatCateringItemName(item.name)}
                                  fill
                                  sizes="76px"
                                  className="object-cover"
                                />
                              </div>
                            ) : null}
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-[15px] font-semibold leading-5 text-[#1f1f1f]">
                                  {formatCateringItemName(item.name)}
                                </h3>
                                {addedCount > 0 ? (
                                  <span className="rounded-full bg-[var(--roma-linen)] px-2 py-0.5 text-[11px] font-bold text-[var(--roma-terracotta)]">
                                    Added {addedCount}
                                  </span>
                                ) : null}
                              </div>
                              {item.description ? (
                                <p className="mt-1 text-[13px] leading-5 text-[#6a6a6a]">
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <p className="shrink-0 text-right text-[15px] font-semibold text-[var(--roma-terracotta)]">
                            {item.price}
                          </p>
                        </div>
                        <button
                          type="button"
                          className={
                            addedCount > 0
                              ? "mt-3 h-11 rounded-[9px] border border-[var(--roma-terracotta)] bg-[var(--roma-linen)] px-3 text-[13px] font-bold text-[var(--roma-terracotta)] transition-colors hover:bg-[var(--roma-bone)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
                              : "mt-3 h-11 rounded-[9px] border border-[#d6d6d6] bg-white px-3 text-[13px] font-bold text-[#1f1f1f] transition-colors hover:bg-[#f7f7f7] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
                          }
                          onClick={() => addCateringPickToRequest(item)}
                        >
                          {addedCount > 0
                            ? `Added ${addedCount}`
                            : "Add to request"}
                        </button>
                      </article>
                    );
                  })}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 pt-2 sm:px-6 md:pb-12">
        <div
          className={
            isRoma
              ? "rounded-[18px] border border-[#e6e6e6] bg-[var(--roma-linen)] p-4"
              : "rounded-[18px] border border-[#d8d8d8] bg-[#fffaf7] p-4"
          }
          aria-labelledby="catering-snapshot-heading"
        >
          <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Catering snapshot
          </p>
          <h2
            id="catering-snapshot-heading"
            className="mt-1 font-serif text-[20px] font-semibold leading-7 text-[#151515]"
          >
            Catering, the {restaurantCopy.shortName} way
          </h2>
          <dl className="mt-4 grid gap-3">
            {cateringSnapshotItems.map((item) => (
              <div
                key={item.label}
                className={
                  isRoma
                    ? "rounded-[12px] border border-[#e6e6e6] bg-white px-3 py-3"
                    : "rounded-[12px] border border-[#eadbd3] bg-white px-3 py-3"
                }
              >
                <dt className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#6f6f6f]">
                  {item.label}
                </dt>
                <dd className="mt-1 text-[14px] leading-6 text-[#333]">{item.detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 pt-2 sm:px-6 md:pb-12">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Catering guides
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {guideLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-[14px] border border-[#d8d8d8] bg-white px-4 py-4 transition hover:border-[var(--roma-terracotta)] hover:bg-[var(--roma-linen)]"
              >
                <span className="block text-[15px] font-semibold leading-5 text-[#1f1f1f]">
                  {link.label}
                </span>
                <span className="mt-2 block text-[13px] leading-5 text-[#666]">
                  {link.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[720px] px-4 pb-10 pt-2 sm:px-6 md:pb-12">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Catering FAQ
          </p>
          <h2 className="mt-2 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
            Quick answers before you request catering
          </h2>
          <div className="mt-6 divide-y divide-[#ededed] border-y border-[#ededed]">
            {faqItems.map((item) => (
              <details key={item.question} className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-4 text-left text-[15px] font-semibold leading-6 text-[#1f1f1f] marker:hidden">
                  <span>{item.question}</span>
                  <span
                    aria-hidden="true"
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#d6d6d6] text-[14px] leading-none text-[var(--roma-terracotta)] transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="pb-4 pr-8 text-[14px] leading-6 text-[#5a5a5a]">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="mx-auto w-full max-w-[720px] px-4 pb-24 pt-4 sm:px-6 md:pb-12">
        <div className="rounded-[24px] bg-[#f6f6f6] px-6 py-10 text-center">
          <Link href={homeHref} className="mx-auto block w-fit" aria-label={`${restaurantCopy.shortName} home`}>
            <Image
              src={footerLogoSrc}
              alt={restaurantCopy.name}
              width={96}
              height={96}
              loading="lazy"
              quality={70}
              className="mx-auto h-auto w-[92px]"
            />
          </Link>

          <nav
            aria-label="Footer navigation"
            className="mt-8 flex flex-col items-center gap-4 text-[15px] font-semibold text-[#1f1f1f]"
          >
            {footerLinks.map((link) => (
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} href={link.href} className="hover:underline">
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          <a
            href={siteConfig.orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mx-auto mt-8 flex h-[48px] w-full max-w-[250px] items-center justify-center rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[14px] font-bold text-[#0d0d0d] transition hover:bg-[var(--roma-saffron-deep)]"
            onClick={() =>
              analytics.orderStarted({
                location: "catering_footer",
                cta_text: "Order online",
                destination_url: siteConfig.orderUrl,
                order_target: isDefaultLocation ? "first_party_order" : "grubhub",
                lead_type: "catering",
                is_external: true,
              })
            }
          >
            Order online
          </a>

          <p className="mx-auto mt-6 max-w-[260px] text-[15px] leading-6 text-[#303030]">
            {isDefaultLocation
              ? "Catering trays and platters for offices, parties, and private events near Southern Highlands."
              : isRoma
                ? "Catering trays and platters for offices, parties, and private events near Southern Highlands or Henderson."
                : `Catering trays and platters for offices, parties, and private events in ${siteConfig.address.city}.`}
          </p>

          <div className="mx-auto mt-5 max-w-[300px] text-[13px] leading-6 text-[#4b4b4b]">
            <p className="font-semibold text-[#303030]">{addressLine}</p>
            <a href={`tel:${phoneHref}`} className="font-semibold text-[var(--roma-terracotta)] hover:underline">
              {phoneDisplay}
            </a>
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-[12px] font-semibold text-[#4b4b4b]">
            <Link href="/terms" className="hover:underline">
              Terms
            </Link>
            <Link href="/privacy" className="hover:underline">
              Privacy
            </Link>
            <Link href="/accessibility" className="hover:underline">
              Accessibility
            </Link>
          </div>

          <p className="mt-7 text-[12px] leading-5 text-[#777]">
            © {new Date().getFullYear()} {restaurantCopy.name}
          </p>
        </div>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-[0.88fr_1.12fr] gap-2 bg-white/95 px-2 pb-2 pt-1 shadow-[0_-10px_30px_-24px_rgba(0,0,0,0.55)] backdrop-blur md:hidden">
        <a
          href={`tel:${phoneHref}`}
          className="flex h-12 items-center justify-center rounded-[9px] border border-[#d6d6d6] bg-white px-4 text-[14px] font-bold text-[#1f1f1f]"
          onClick={() =>
            analytics.phoneClicked({
              location: "catering_sticky_mobile",
              cta_text: "Call",
              destination_url: `tel:${phoneHref}`,
              lead_type: "catering",
            })
          }
        >
          Call
        </a>
        {cateringOrderingLive ? (
          <Link
            href="/order/catering"
            className="roma-lead-primary-action flex h-12 items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-4 text-[14px] font-bold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-saffron-deep)]"
          >
            Order Catering
          </Link>
        ) : (
          <a
            href="#catering-request-form"
            className="roma-lead-primary-action flex h-12 items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-4 text-[14px] font-bold text-[#0d0d0d] transition-colors hover:bg-[var(--roma-saffron-deep)]"
            onClick={() => analytics.inquiryStarted(eventType, "catering_sticky_mobile")}
          >
            Request Catering
          </a>
        )}
      </div>
    </main>
  );
}

function SaffronField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="block text-[15px] font-normal leading-6 text-[#232323]">
        {label}
      </label>
      {children}
    </div>
  );
}
